import styles from './Timeslicer.module.scss';
import {
    calculateMonthSelection,
    getMonthYearText,
    handleDraggingCursor
} from './helpers';
import leftSlider from './images/left-slider.svg';
import rightSlider from './images/right-slider.svg';
import { features } from 'config/features';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { increaseEndDateByOneMonth } from 'utils/helpers';

const Timeslicer = ({ monthList, onChange, externalReset, start, end }) => {
    /**
     * Check feature flag.
     */
    if (!features.timeslicer) {
        return null;
    }

    /**
     * Constants.
     */
    const S_WIDTH = 30; // Slider width
    const M_WIDTH = 38; // Month width
    const N_MONTHS = 12; // Number of months

    const LP_START = S_WIDTH;
    const RP_START = LP_START + M_WIDTH * N_MONTHS;

    const leftPickerRef = useRef(null);
    const rightPickerRef = useRef(null);
    const [startMonthIndex, setStartMonthIndex] = useState(0);
    const [endMonthIndex, setEndMonthIndex] = useState(11);
    const [leftPickerX, setLeftPickerX] = useState(LP_START);
    const [rightPickerX, setRightPickerX] = useState(RP_START);
    const [leftPickerBound, setLeftPickerBound] = useState({
        left: LP_START,
        right: RP_START - M_WIDTH
    });
    const [rightPickerBound, setRightPickerBound] = useState({
        right: RP_START,
        left: LP_START + M_WIDTH
    });

    useEffect(() => {
        if (typeof externalReset === 'boolean') {
            resetTimeSlicerUI();
        }
    }, [externalReset]);

    useEffect(() => {
        if (start) {
            const startIndex = monthList.findIndex(
                (m) => m.epoch === start.epoch
            );
            setStart(startIndex);
        }

        if (end) {
            const endIndex = monthList.findIndex(
                (m) => increaseEndDateByOneMonth(m).epoch === end.epoch
            );
            setEnd(endIndex);
        }
    }, [start, end]);

    /**
     * This will reset the timeslicer UI
     */
    const resetTimeSlicerUI = () => {
        setLeftPickerX(LP_START);
        setRightPickerX(RP_START);

        setStartMonthIndex(0);
        setEndMonthIndex(11);

        setLeftPickerBound({
            left: LP_START,
            right: RP_START - M_WIDTH
        });
        setRightPickerBound({
            right: RP_START,
            left: LP_START + M_WIDTH
        });

        onTimeSlicerChange(0, 11);
    };

    const setStart = (month) => {
        const leftPosition = LP_START + M_WIDTH * month;
        setStartMonthIndex(month);
        setLeftPickerX(leftPosition);
        setRightPickerBound({
            right: RP_START,
            left: leftPosition + M_WIDTH
        });
    };

    const setEnd = (month) => {
        const leftPosition = LP_START + M_WIDTH * month;
        setEndMonthIndex(month);
        setRightPickerX(leftPosition + M_WIDTH);
        setLeftPickerBound({
            left: LP_START,
            right: leftPosition
        });
    };

    const calculateFliteredMonths = (lx, rx, start, gap) => {
        const startIndex = (lx - start) / gap;
        const endIndex = (rx - start - gap) / gap;

        setStartMonthIndex(startIndex);
        setEndMonthIndex(endIndex);
    };

    const onTimeSlicerChange = (startIndex, endIndex) => {
        if (onChange) {
            onChange(monthList[startIndex], monthList[endIndex]);
        }
    };

    const pickerOptions = {
        axis: 'x',
        grid: [38, 0],
        defaultClassNameDragging: styles.pickerDragging,
        defaultClassNameDragged: styles.pickerDragged,
        onStart: () => {
            handleDraggingCursor(styles.graddingCursor, true);
        },
        // eslint-disable-next-line no-unused-vars
        onStop: (event, node) => {
            handleDraggingCursor(styles.graddingCursor, false);

            /**
             * This calls exposed onChange method.
             */
            onTimeSlicerChange(startMonthIndex, endMonthIndex);
        }
    };

    const leftPickerOptions = {
        ...pickerOptions,
        defaultPosition: { x: LP_START, y: 0 },
        position: { x: leftPickerX, y: 0 },
        bounds: leftPickerBound,
        onDrag: (event, node) => {
            /**
             * Here we set the left boundry for right picker
             * on left picker movement.
             */
            setRightPickerBound({
                ...rightPickerBound,
                left: node.x + M_WIDTH
            });
            setLeftPickerX(node.x);
            calculateFliteredMonths(node.x, rightPickerX, LP_START, M_WIDTH);
        }
    };

    const rightPickerOptions = {
        ...pickerOptions,
        defaultPosition: { x: RP_START, y: 0 },
        position: { x: rightPickerX, y: 0 },
        bounds: rightPickerBound,
        onDrag: (event, node) => {
            /**
             * Here we set the right boundry for left picker
             * on right picker movement.
             */
            setLeftPickerBound({
                ...leftPickerBound,
                right: node.x - M_WIDTH
            });
            setRightPickerX(node.x);
            calculateFliteredMonths(leftPickerX, node.x, LP_START, M_WIDTH);
        }
    };

    return (
        monthList.length > 0 && (
            <div className={styles.wrapper}>
                <div className={styles.controller}>
                    <div className={styles.leftSlider}>
                        <img src={leftSlider} alt="" />
                    </div>
                    <Draggable nodeRef={leftPickerRef} {...leftPickerOptions}>
                        <div
                            ref={leftPickerRef}
                            className={styles.leftPicker}
                        ></div>
                    </Draggable>

                    <div className={styles.monthWrapper}>
                        {monthList.map((m, i) => (
                            <div
                                key={`month-${i}`}
                                className={`${
                                    styles.month
                                } ${calculateMonthSelection(
                                    i,
                                    startMonthIndex,
                                    endMonthIndex,
                                    styles.selected
                                )}`}
                            >
                                {m.monthText}
                            </div>
                        ))}
                    </div>
                    <Draggable nodeRef={rightPickerRef} {...rightPickerOptions}>
                        <div
                            ref={rightPickerRef}
                            className={styles.rightPicker}
                        ></div>
                    </Draggable>
                    <div className={styles.rightSlider}>
                        <img src={rightSlider} alt="" />
                    </div>
                </div>
                <div className={styles.filteredMonth}>
                    <div className={styles.monthGap}>
                        {`${getMonthYearText(
                            monthList,
                            startMonthIndex
                        )} - ${getMonthYearText(monthList, endMonthIndex)}`}
                    </div>
                    <div className={styles.filterCriteria}>Month</div>
                </div>
            </div>
        )
    );
};

Timeslicer.propTypes = {
    monthList: PropTypes.array,
    onChange: PropTypes.func,
    externalReset: PropTypes.bool,
    start: PropTypes.shape({ epoch: PropTypes.number }),
    end: PropTypes.shape({ epoch: PropTypes.number })
};

export default Timeslicer;
